/**
 * Finestrella "Ricontatta": domani, tra 3 giorni, tra una settimana o data a scelta,
 * con una nota facoltativa. In un portale sopra tutto, tenuta dentro lo schermo;
 * si chiude con Escape e col clic fuori. Serve all'intestazione, al menu della riga e a "Rimanda".
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BellRing, Check, Loader2 } from 'lucide-react';
import { tip } from '@/components/messages/ChatTooltip';
import {
  RECONTACT_CHOICES, formatRecontactDate, minDateInput, recontactDueFor, recontactDueFromDateInput,
} from '@/lib/services/whatsapp/recontactRules';
import { useChatRecontact } from './useChatRecontact';

interface Props {
  x: number;
  y: number;
  chatKey: string;
  chatName: string;
  organizationId: string | null | undefined;
  onClose: () => void;
}

const btnCls = 'w-full px-3 py-1.5 text-left text-sm rounded-md text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed';

export function RecontactPicker({ x, y, chatKey, chatName, organizationId, onClose }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });
  const { reminder, linked, canSave, schedule, complete } = useChatRecontact(chatKey, organizationId);
  const [note, setNote] = useState(reminder?.note ?? '');
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      left: Math.max(8, Math.min(x, window.innerWidth - r.width - 8)),
      top: Math.max(8, Math.min(y, window.innerHeight - r.height - 8)),
    });
  }, [x, y]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = async (dueAt: Date | null) => {
    if (!dueAt || saving) return;
    setSaving(true);
    const ok = await schedule(chatName, dueAt, note || null);
    setSaving(false);
    if (ok) onClose();
  };

  const done = async () => {
    setSaving(true);
    const ok = await complete();
    setSaving(false);
    if (ok) onClose();
  };

  const off = !canSave || saving;
  const customDue = recontactDueFromDateInput(date);

  return createPortal(
    <>
      <div className="fixed inset-0" style={{ zIndex: 9999 }} onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} />
      <div
        ref={ref}
        role="dialog"
        aria-label="Ricontatta"
        className="fixed w-[260px] p-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.09)] dark:border-white/10"
        style={{ zIndex: 9999, left: pos.left, top: pos.top }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-1 pb-1.5 text-sm font-medium text-[#37352F] dark:text-white">
          <BellRing className="w-4 h-4 text-[#D9730D]" />
          {reminder ? 'Rimanda il ricontatto' : 'Ricontatta'}
        </div>
        {reminder && (
          <p className="px-1 pb-1.5 text-[11px] text-[#787774]">Previsto il {formatRecontactDate(reminder.dueAt)}{reminder.taskId ? ' (Task commerciale)' : ''}</p>
        )}
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={300}
          placeholder="Nota (facoltativa)"
          className="w-full mb-1.5 px-2 py-1.5 text-sm rounded-md border border-[rgba(55,53,47,0.16)] dark:border-white/15 bg-transparent text-[#37352F] dark:text-white outline-none focus:border-[#2383E2]"
        />
        {RECONTACT_CHOICES.map(({ value, label }) => (
          <button key={value} type="button" className={btnCls} disabled={off} onClick={() => void save(recontactDueFor(value))}>
            {label}<span className="float-right text-[11px] text-[#9B9A97]">{formatRecontactDate(recontactDueFor(value).toISOString())}</span>
          </button>
        ))}
        <div className="flex items-center gap-1.5 mt-1 px-1">
          <input
            type="date"
            value={date}
            min={minDateInput()}
            onChange={(e) => setDate(e.target.value)}
            aria-label="Data a scelta"
            {...tip('Data a scelta (alle 9:00)')}
            className="flex-1 min-w-0 px-2 py-1 text-sm rounded-md border border-[rgba(55,53,47,0.16)] dark:border-white/15 bg-transparent text-[#37352F] dark:text-white outline-none focus:border-[#2383E2]"
          />
          <button type="button" disabled={off || !customDue} onClick={() => void save(customDue)}
            className="px-2.5 py-1 text-xs font-medium rounded-md bg-[#2383E2] text-white hover:bg-[#1a6fc2] disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Imposta'}
          </button>
        </div>
        {reminder && (
          <button type="button" disabled={saving} onClick={() => void done()} className={`${btnCls} mt-1.5 flex items-center gap-2 border-t border-[rgba(55,53,47,0.09)] dark:border-white/10 rounded-none pt-2`}>
            <Check className="w-4 h-4 text-[#0F7B6C]" />Fatto, togli il promemoria
          </button>
        )}
        <p className="px-1 pt-1.5 text-[11px] text-[#787774]">
          {!canSave
            ? 'Per le chat non collegate a un Lead o Cliente il promemoria sarà disponibile a breve: collega la chat per salvarlo come Task commerciale.'
            : linked ? 'Crea un Task commerciale sul Lead/Cliente: lo vedi anche nelle attività del CRM.'
            : 'La chat non è collegata a un Lead o Cliente: il promemoria resta sulla chat.'}
        </p>
      </div>
    </>,
    document.body,
  );
}
